import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { LOCATION_TYPE } from "../../Location/Data";
import CustomLayout from "../../../Core/Layout/CustomLayout";
import Button from "../../../Core/Components/CustomButton";
import { PopoverDropdown } from "../../../Core/Components/CustomDropdown";
import { CustomInputWithController } from "../../../Core/Components/CustomInput";
import {
  addAgriTechRate,
  editAgriTechRate,
} from "../../../Redux/APIs/ratesAPI";
import { updateRates } from "../../../Redux/Slices/rates.slice";
import capitalize from "../../../Utils/capitalize";

const LabelAndInput = ({
  label,
  control,
  formState,
  name,
  placeholder,
  rules,
  focus,
  setFocus,
  showUnit = true,
}) => {
  return (
    <div className="pt-6">
      <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
        {label}
      </p>

      <div className="flex items-center gap-x-3">
        <CustomInputWithController
          intent="createUser"
          control={control}
          formState={formState}
          focus={focus}
          setFocus={(e) => setFocus(e)}
          name={name}
          placeholder={placeholder}
          rules={rules}
        />
        {showUnit && <p className="font-aileron text-xs font-normal">KSH</p>}
      </div>
    </div>
  );
};

const AddAgriTechRate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { allLocations } = useSelector((state) => state.locations);

  const { currentAgriTechRate } = useSelector((state) => state.rates);

  const hubStations = allLocations
    .filter((item) => item.type === LOCATION_TYPE.HUB_STATION)
    .map((location) => ({
      id: location.id,
      value: location.name,
    }));

  const { handleSubmit, control, formState, setValue, clearErrors, setError } =
    useForm({
      defaultValues: {
        hubId: id ? currentAgriTechRate?.hubId : null,
        hubName: id ? currentAgriTechRate?.hubName : "",
        rate: id ? currentAgriTechRate?.rate : null,
      },
    });

  const [focus, setFocus] = useState(false);

  const hubNameError = formState?.errors?.hubName?.message;

  const rules = {
    required: "Required.",
    validate: {
      isNumber: (value) => {
        return value > 0 || "Please enter valid number";
      },
    },
  };

  const onCancel = () => {
    navigate(-1);
  };

  const onSubmit = async (e) => {
    const payload = e;

    if (id) {
      payload.agriTechRateId = id;

      const editResponse = await dispatch(editAgriTechRate(payload));

      if (
        editResponse?.payload?.response &&
        editResponse?.payload?.response?.status !== 200
      ) {
        setError("hubName", {
          type: "server",
          shouldFocus: true,
          message: editResponse?.payload?.response?.data?.message,
        });
      } else {
        dispatch(
          updateRates({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Agri Tech Rate Edited",
                description: `Rate for Agri Tech at ${capitalize(
                  e.hubName,
                )} was edited successfully`,
              },
            },
          }),
        );

        navigate("/rates?tab=agri-tech");
      }
    } else {
      const addResponse = await dispatch(addAgriTechRate(payload));

      if (
        addResponse?.payload?.response &&
        addResponse?.payload?.response?.status !== 200
      ) {
        setError("hubName", {
          type: "server",
          shouldFocus: true,
          message: addResponse?.payload?.response?.data?.message,
        });
      } else {
        dispatch(
          updateRates({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "New Agri Tech Rate Added",
                description: `Rate for Agri Tech at ${capitalize(
                  e.hubName,
                )} was added successfully`,
              },
            },
          }),
        );

        navigate("/rates?tab=agri-tech");
      }
    }
  };

  return (
    <CustomLayout pageName="Rates Manager" hideFooter>
      <div className="px-6 pt-6 bg-background-white">
        <div className="flex items-center justify-between pb-6">
          <p className="font-poppins text-base font-medium capitalize">
            Add commission rate
          </p>

          <div className="flex items-center gap-x-3">
            <Button
              size="filter"
              width="generateUser"
              colors="grey"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="dynamic"
              colors="primary"
              onClick={handleSubmit(onSubmit)}
            >
              {id ? "Save Changes" : "Add Commission Rate"}
            </Button>
          </div>
        </div>

        <div className="w-full bg-light-grey h-[1px]" />
      </div>

      <div className="h-[calc(100vh-72px-89px)] overflow-y-scroll px-6 pb-6 bg-background-white">
        <div className="mt-6">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Hub Station
          </p>

          <Controller
            name="hubName"
            control={control}
            rules={{ required: "Required." }}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                searchPlaceholder="Search Hub Station"
                placeholder="Assign Hub Station"
                className="max-h-[300px] overflow-y-scroll"
                formState={formState}
                options={hubStations}
                onSelect={(data) => {
                  setValue("hubName", data.value);
                  setValue("hubId", data.id);
                  clearErrors("hubName");
                }}
              />
            )}
          />

          <p
            className={`font-poppins text-xs font-light capitalize leading-4.5 ${
              hubNameError ? "text-alert-red visible mt-2" : "invisible"
            }`}
          >
            {hubNameError}
          </p>
        </div>

        <LabelAndInput
          label="Set a rate for commission"
          control={control}
          formState={formState}
          name="rate"
          placeholder="Enter A Rate"
          rules={rules}
          focus={focus}
          setFocus={(state) => setFocus(state)}
        />
      </div>
    </CustomLayout>
  );
};

export default AddAgriTechRate;
